import { useState } from "react";

import {
  ESCOPOS,
  JANELA_DEDUP_MAX,
  JANELA_DEDUP_MIN,
  JANELA_DIAS_MIN,
  SEVERIDADES,
  TIPOS_SALDO,
  type EscopoRegra,
  type Regra,
  type RegraIn,
  type RegraPatch,
  type SeveridadeRegra,
  type TipoSaldo,
} from "../../lib/config-alertas/tipos";
import { useCanais } from "../../lib/config-alertas/hooks";
import { useEmpresasAlertas } from "../../lib/alertas/hooks";
import { useContas } from "../../lib/cadastros/hooks";
import { normalizarDecimalEntrada } from "../../lib/formato/decimal";
import { ErroHttp } from "../../lib/http/erros";

/*
 * Formulário controlado de REGRA de alerta (FE-6b — Tela 6). Cobre os DOIS modos
 * (espelha `FormularioConta`/`FormularioCanal`):
 *
 *  - CRIAÇÃO (`regra === undefined`): todos os campos. `empresa_id` derivado do
 *    escopo + da empresa selecionada; `conta_id` só no escopo de conta.
 *  - EDIÇÃO (`regra` presente): `empresa_id` é IMUTÁVEL no backend → a empresa é
 *    READ-ONLY (mostra o nome, sem select) e NÃO vai no PATCH. Os demais campos são
 *    editáveis, inicializados pelos valores da regra.
 *
 * CONDICIONAIS (replicam a maquete):
 *  - `showEmpresa = escopo !== "grupo"`; `showConta = escopo === "conta"`.
 *  - 🔴 D19: `tipo_saldo === "projetado" && escopo === "conta"` é PROIBIDO pelo
 *    backend → exibe aviso (`role="alert"`) e DESABILITA "Salvar". A UI previne o
 *    óbvio; o backend é a autoridade (422/404 nas combinações cruzadas).
 *
 * 🔴 RIGOR FINANCEIRO: o `limite` é digitado como texto e normalizado por
 * `normalizarDecimalEntrada` (string-based) no submit — NUNCA `Number`/`parseFloat`.
 * `null` (formato inválido) → erro de borda e NÃO submete.
 *
 * Componente PURO: não conhece o hook de mutação. O orquestrador (`SecaoRegras`)
 * injeta `aoCriar`/`aoEditar`, `salvando` e o `erro` normalizado. SÓ tokens CPMH.
 */

const ESCOPO_GRUPO: EscopoRegra = "grupo";
const ESCOPO_CONTA: EscopoRegra = "conta";

const JANELA_DEDUP_DEFAULT = 12;

interface FormularioRegraProps {
  /** Regra em edição, ou `undefined` para criação. */
  regra?: Regra;
  /** Cria uma nova regra (modo criação). */
  aoCriar: (corpo: RegraIn) => void;
  /** Edita a regra (modo edição). `corpo` OMITE `empresa_id` (imutável). */
  aoEditar: (corpo: RegraPatch) => void;
  aoCancelar: () => void;
  salvando: boolean;
  erro: unknown;
}

export function FormularioRegra({
  regra,
  aoCriar,
  aoEditar,
  aoCancelar,
  salvando,
  erro,
}: FormularioRegraProps): JSX.Element {
  const ehEdicao = regra !== undefined;

  const [nome, setNome] = useState(regra?.nome ?? "");
  const [tipoSaldo, setTipoSaldo] = useState<TipoSaldo>(
    regra?.tipo_saldo ?? TIPOS_SALDO[0].valor,
  );
  const [escopo, setEscopo] = useState<EscopoRegra>(
    regra?.escopo ?? ESCOPOS[0].valor,
  );
  const [severidade, setSeveridade] = useState<SeveridadeRegra>(
    regra?.severidade ?? SEVERIDADES[0].valor,
  );
  const [limite, setLimite] = useState(regra?.limite ?? "");
  const [janelaDias, setJanelaDias] = useState(
    String(regra?.janela_dias ?? JANELA_DIAS_MIN),
  );
  const [janelaDedupHoras, setJanelaDedupHoras] = useState(
    String(regra?.janela_dedup_horas ?? JANELA_DEDUP_DEFAULT),
  );
  const [ativa, setAtiva] = useState(regra?.ativa ?? true);

  // Empresa/conta selecionadas. Na criação começam vazias; na edição a empresa é
  // read-only (vinda da regra) e a conta inicializa com a da regra.
  const [empresaSelecionada, setEmpresaSelecionada] = useState(
    regra?.empresa_id ?? "",
  );
  const [contaSelecionada, setContaSelecionada] = useState(
    regra?.conta_id ?? "",
  );

  // Canais marcados (ids). Na edição inicializa com os ids da regra.
  const [canaisMarcados, setCanaisMarcados] = useState<string[]>(
    regra?.canais ?? [],
  );

  const [erroBorda, setErroBorda] = useState<string | null>(null);

  const consultaCanais = useCanais();
  const consultaEmpresas = useEmpresasAlertas();
  // Na edição a empresa vem fixa da regra; na criação, da seleção. Em ambos, as
  // contas dependem da empresa em foco (idle enquanto não houver empresa).
  const empresaEmFoco = ehEdicao ? (regra.empresa_id ?? "") : empresaSelecionada;
  const consultaContas = useContas(empresaEmFoco === "" ? null : empresaEmFoco);

  const showEmpresa = escopo !== ESCOPO_GRUPO;
  const showConta = escopo === ESCOPO_CONTA;
  // D19: saldo projetado não pode ter escopo de conta.
  const violaD19 = tipoSaldo === "projetado" && escopo === ESCOPO_CONTA;

  // Escopos OFERECIDOS no select. Na CRIAÇÃO, os três. Na EDIÇÃO, o conjunto é
  // restrito pelo `empresa_id` ATUAL da regra, que é IMUTÁVEL no PATCH (sem isso,
  // mudar o escopo levaria a um 422/404 genérico — beco sem saída):
  //  - regra de GRUPO (`empresa_id === null`) → só "grupo" (virar empresa/conta
  //    exigiria ADICIONAR empresa, imutável);
  //  - regra de EMPRESA (`empresa_id !== null`) → {"empresa","conta"} (nunca
  //    "grupo", que exigiria `empresa_id=null`, imutável).
  // A restrição é só de OPÇÕES: submit/D19/normalização seguem válidos para as
  // opções remanescentes.
  const escoposOferecidos = !ehEdicao
    ? ESCOPOS
    : regra.empresa_id === null
      ? ESCOPOS.filter((opcao) => opcao.valor === ESCOPO_GRUPO)
      : ESCOPOS.filter((opcao) => opcao.valor !== ESCOPO_GRUPO);

  function alternarCanal(id: string, marcado: boolean): void {
    setCanaisMarcados((atuais) =>
      marcado ? [...atuais, id] : atuais.filter((x) => x !== id),
    );
  }

  function submeter(): void {
    if (violaD19) {
      // Defesa extra: o botão já fica disabled, mas não submete de qualquer forma.
      return;
    }
    if (nome.trim() === "") {
      setErroBorda("Informe o nome da regra.");
      return;
    }

    const limiteCanonico = normalizarDecimalEntrada(limite);
    if (limiteCanonico === null) {
      setErroBorda("Limite inválido.");
      return;
    }

    if (showEmpresa && empresaSelecionada === "" && !ehEdicao) {
      setErroBorda("Selecione a empresa.");
      return;
    }
    if (showConta && contaSelecionada === "") {
      setErroBorda("Selecione a conta.");
      return;
    }

    const janelaDiasNum = inteiroDe(janelaDias, JANELA_DIAS_MIN);
    const janelaDedupNum = inteiroDe(janelaDedupHoras, JANELA_DEDUP_DEFAULT);

    setErroBorda(null);

    if (!ehEdicao) {
      const empresaId = escopo === ESCOPO_GRUPO ? null : empresaSelecionada;
      const contaId = escopo === ESCOPO_CONTA ? contaSelecionada : null;
      aoCriar({
        nome: nome.trim(),
        tipo_saldo: tipoSaldo,
        escopo,
        limite: limiteCanonico,
        severidade,
        canais: canaisMarcados,
        janela_dias: janelaDiasNum,
        janela_dedup_horas: janelaDedupNum,
        empresa_id: empresaId,
        conta_id: contaId,
        ativa,
      });
      return;
    }

    // EDIÇÃO: `empresa_id` NÃO vai no PATCH (imutável). `conta_id` segue o escopo.
    const contaId = escopo === ESCOPO_CONTA ? contaSelecionada : null;
    aoEditar({
      nome: nome.trim(),
      tipo_saldo: tipoSaldo,
      escopo,
      limite: limiteCanonico,
      severidade,
      canais: canaisMarcados,
      janela_dias: janelaDiasNum,
      janela_dedup_horas: janelaDedupNum,
      conta_id: contaId,
      ativa,
    });
  }

  const empresas = consultaEmpresas.data ?? [];
  const contas = consultaContas.data ?? [];
  const canais = consultaCanais.data ?? [];

  const nomeEmpresaAtual =
    empresas.find((empresa) => empresa.id === regra?.empresa_id) ?? null;
  const rotuloEmpresaEdicao =
    nomeEmpresaAtual !== null
      ? (nomeEmpresaAtual.fantasia ?? nomeEmpresaAtual.razao_social)
      : (regra?.empresa_id ?? "—");

  return (
    <div className="flex max-w-lg flex-col gap-4 rounded-md border border-platinum bg-snow p-6 font-corpo text-base text-off-black">
      <h2 className="font-titulo text-xl text-off-black">
        {ehEdicao ? "Editar regra" : "Nova regra"}
      </h2>

      <label className="flex flex-col gap-1">
        <span className="font-subtitulo">Nome</span>
        <input
          type="text"
          value={nome}
          onChange={(evento) => setNome(evento.target.value)}
          className="rounded-md border border-platinum bg-snow px-2 py-1 text-off-black"
        />
      </label>

      <label className="flex flex-col gap-1">
        <span className="font-subtitulo">Tipo de saldo</span>
        <select
          value={tipoSaldo}
          onChange={(evento) => setTipoSaldo(evento.target.value as TipoSaldo)}
          className="rounded-md border border-platinum bg-snow px-2 py-1 text-off-black"
        >
          {TIPOS_SALDO.map((opcao) => (
            <option key={opcao.valor} value={opcao.valor}>
              {opcao.rotulo}
            </option>
          ))}
        </select>
      </label>

      <label className="flex flex-col gap-1">
        <span className="font-subtitulo">Escopo</span>
        <select
          value={escopo}
          onChange={(evento) => setEscopo(evento.target.value as EscopoRegra)}
          className="rounded-md border border-platinum bg-snow px-2 py-1 text-off-black"
        >
          {escoposOferecidos.map((opcao) => (
            <option key={opcao.valor} value={opcao.valor}>
              {opcao.rotulo}
            </option>
          ))}
        </select>
      </label>

      {showEmpresa &&
        (ehEdicao ? (
          <div className="flex flex-col gap-1">
            <span className="font-subtitulo">Empresa</span>
            <span>{rotuloEmpresaEdicao}</span>
          </div>
        ) : (
          <label className="flex flex-col gap-1">
            <span className="font-subtitulo">Empresa</span>
            <select
              value={empresaSelecionada}
              onChange={(evento) => {
                setEmpresaSelecionada(evento.target.value);
                // Trocar a empresa invalida a conta antes escolhida.
                setContaSelecionada("");
              }}
              className="rounded-md border border-platinum bg-snow px-2 py-1 text-off-black"
            >
              <option value="" disabled>
                Selecione…
              </option>
              {empresas.map((empresa) => (
                <option key={empresa.id} value={empresa.id}>
                  {empresa.fantasia ?? empresa.razao_social}
                </option>
              ))}
            </select>
          </label>
        ))}

      {showConta && (
        <label className="flex flex-col gap-1">
          <span className="font-subtitulo">Conta</span>
          <select
            value={contaSelecionada}
            onChange={(evento) => setContaSelecionada(evento.target.value)}
            className="rounded-md border border-platinum bg-snow px-2 py-1 text-off-black"
          >
            <option value="" disabled>
              Selecione…
            </option>
            {contas.map((conta) => (
              <option key={conta.id} value={conta.id}>
                {conta.agencia} / {conta.numero}
              </option>
            ))}
          </select>
        </label>
      )}

      <label className="flex flex-col gap-1">
        <span className="font-subtitulo">Limite</span>
        <input
          type="text"
          value={limite}
          onChange={(evento) => setLimite(evento.target.value)}
          className="rounded-md border border-platinum bg-snow px-2 py-1 text-off-black"
        />
      </label>

      <label className="flex flex-col gap-1">
        <span className="font-subtitulo">Severidade</span>
        <select
          value={severidade}
          onChange={(evento) =>
            setSeveridade(evento.target.value as SeveridadeRegra)
          }
          className="rounded-md border border-platinum bg-snow px-2 py-1 text-off-black"
        >
          {SEVERIDADES.map((opcao) => (
            <option key={opcao.valor} value={opcao.valor}>
              {opcao.rotulo}
            </option>
          ))}
        </select>
      </label>

      <label className="flex flex-col gap-1">
        <span className="font-subtitulo">Janela (dias)</span>
        <input
          type="number"
          min={JANELA_DIAS_MIN}
          value={janelaDias}
          onChange={(evento) => setJanelaDias(evento.target.value)}
          className="rounded-md border border-platinum bg-snow px-2 py-1 text-off-black"
        />
      </label>

      <label className="flex flex-col gap-1">
        <span className="font-subtitulo">Janela de dedup (horas)</span>
        <input
          type="number"
          min={JANELA_DEDUP_MIN}
          max={JANELA_DEDUP_MAX}
          value={janelaDedupHoras}
          onChange={(evento) => setJanelaDedupHoras(evento.target.value)}
          className="rounded-md border border-platinum bg-snow px-2 py-1 text-off-black"
        />
      </label>

      <fieldset className="flex flex-col gap-1">
        <legend className="font-subtitulo">Canais</legend>
        {canais.length === 0 ? (
          <span className="text-off-black">Nenhum canal disponível.</span>
        ) : (
          canais.map((canal) => (
            <label key={canal.id} className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={canaisMarcados.includes(canal.id)}
                onChange={(evento) =>
                  alternarCanal(canal.id, evento.target.checked)
                }
              />
              <span>{canal.nome}</span>
            </label>
          ))
        )}
      </fieldset>

      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={ativa}
          onChange={(evento) => setAtiva(evento.target.checked)}
        />
        <span className="font-subtitulo">Ativa</span>
      </label>

      {violaD19 && (
        <p role="alert" className="font-corpo text-base text-flame">
          Saldo projetado não pode ter escopo de conta.
        </p>
      )}

      {erroBorda !== null && (
        <p role="alert" className="font-corpo text-base text-flame">
          {erroBorda}
        </p>
      )}

      {erro !== null && erro !== undefined && (
        <p role="alert" className="font-corpo text-base text-flame">
          {mensagemErroRegra(erro)}
        </p>
      )}

      <div className="flex justify-end gap-3">
        <button
          type="button"
          onClick={aoCancelar}
          disabled={salvando}
          className="rounded-md border border-platinum bg-snow px-4 py-1 text-off-black disabled:opacity-60"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={submeter}
          disabled={salvando || violaD19}
          className="rounded-md border border-flame bg-flame px-4 py-1 text-snow disabled:opacity-60"
        >
          {salvando ? "Salvando…" : "Salvar"}
        </button>
      </div>
    </div>
  );
}

/**
 * Converte o texto de um input number num inteiro ≥ 0, com `padrao` quando vazio ou
 * inválido. String-based no descarte do não-numérico; o backend valida os limites
 * de fato (janela_dias ≥ 1; dedup 1..168). NÃO é money — `janela_*` são contadores
 * inteiros, então `Number` aqui é seguro (não há perda de precisão de centavo).
 */
function inteiroDe(texto: string, padrao: number): number {
  const limpo = texto.trim();
  if (limpo === "" || !/^\d+$/.test(limpo)) {
    return padrao;
  }
  return Number(limpo);
}

/**
 * Traduz o erro de uma mutação de regra numa mensagem ESTÁVEL e clara. 409 (nome
 * duplicado) e 422/404 (validação cruzada — escopo/conta/D19) recebem mensagens
 * claras; demais reusam a mensagem genérica do `ErroHttp`. NUNCA ecoa o corpo do
 * servidor. Erro não-`ErroHttp` → fallback.
 */
function mensagemErroRegra(erro: unknown): string {
  if (erro instanceof ErroHttp) {
    if (erro.status === 409) {
      return "Já existe uma regra com este nome.";
    }
    if (erro.status === 422 || erro.status === 404) {
      return "Dados inválidos. Verifique os campos e tente novamente.";
    }
    return erro.message;
  }
  return "Não foi possível salvar a regra.";
}
